/**
 * The recorder's input meter (UC-8, light): the loudness of what the microphone hears, as the
 * last 32 bars. The analyser hands over a frame of the waveform as unsigned bytes around 128; a
 * frame's RMS is its loudness, and its root lifts speech — which sits low on a linear scale —
 * to where a bar shows it. Pure.
 */

/** How many bars the meter draws: the newest on the right. */
export const LEVEL_BARS = 32

/** The meter before anything is heard. */
export const SILENT_LEVELS: readonly number[] = Array.from({ length: LEVEL_BARS }, () => 0)

/** The midpoint of an unsigned 8-bit sample: silence. */
const BYTE_MIDPOINT = 128

/** How much the root of the RMS is stretched, so a normal voice fills about half a bar. */
const SPEECH_GAIN = 1.5

/** RMS of a frame of unsigned 8-bit samples (the analyser's time-domain bytes), 0..1. */
export function rmsOfBytes(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i++) {
    const sample = (samples[i] - BYTE_MIDPOINT) / BYTE_MIDPOINT
    sum += sample * sample
  }
  return Math.sqrt(sum / samples.length)
}

/** The height of the bar for an RMS, 0..1. */
export function meterLevel(rms: number): number {
  return Math.min(1, Math.sqrt(Math.max(0, rms)) * SPEECH_GAIN)
}

/**
 * The true level beside the bar (ADR-0062, the voice sample): the analyser's frame as floats, in
 * dB under full scale, and whether it clips. The bytes above cannot say either: they round a quiet
 * room to one or two codes — about −45 dBFS, whatever the room — and are lifted for the eye. The
 * questioning's meter reads none of this.
 */

/** What silence reads as: no frame is quieter, and a frame of zeros reads exactly this. */
export const SILENCE_DBFS = -100

/** A sample this near full scale is at the rail: the converter had no more room. */
export const CLIP_SAMPLE = 0.985

/** One sample at the rail is a peak; this many in a frame is a clipped wave. */
export const CLIP_MIN_SAMPLES = 3

/** RMS of a frame of float samples (−1..1), 0..1. */
export function rmsOfFloats(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0
  let sum = 0
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i]
  return Math.sqrt(sum / samples.length)
}

/** An RMS in dB under full scale: 1 → 0, 0.1 → −20, and nothing under `SILENCE_DBFS`. */
export function dbfsOfRms(rms: number): number {
  if (!(rms > 0)) return SILENCE_DBFS
  return Math.max(SILENCE_DBFS, 20 * Math.log10(rms))
}

/** Whether a frame of float samples clips: enough of them stand at the rail. */
export function isClipping(samples: ArrayLike<number>): boolean {
  let atRail = 0
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]) >= CLIP_SAMPLE) atRail += 1
    if (atRail >= CLIP_MIN_SAMPLES) return true
  }
  return false
}

/** What one frame says of the sound itself, beside the height of its bar. */
export interface LevelSignal {
  /** The frame's RMS in dB under full scale, `SILENCE_DBFS`..0. */
  dbfs: number
  /** The frame clips. */
  clipping: boolean
}

/** The true level and the clipping sign of a frame of float samples. */
export function signalOfFloats(samples: ArrayLike<number>): LevelSignal {
  return { dbfs: dbfsOfRms(rmsOfFloats(samples)), clipping: isClipping(samples) }
}

/** The bars after a new level: the newest last, the oldest dropped once there are `size`. */
export function pushLevel(
  levels: readonly number[],
  level: number,
  size: number = LEVEL_BARS
): number[] {
  return [...levels, level].slice(-size)
}
